import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import appointmentService from "portal/services/appointmentService";
import { announceTwice, isAnnouncerUnlocked, armAnnouncerUnlock } from "portal/utils/sessionAnnouncer";

const STORAGE_KEY = "vistaar_session_announcer";

// Don't re-fetch more than once in this window, however many times the user
// tabs away and back.
const REFRESH_MIN_GAP_MS = 10 * 60 * 1000;

const CHIME_TICK_MS = 20 * 1000;

// A session chimes if it started within this many minutes. The tick used to need the
// exact minute, and a browser slows timers in a background tab (to once a minute or
// less), so a tick could miss the minute and the session never chimed.
const CHIME_WINDOW_MIN = 2;

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/**
 * App-wide session-start announcer. Lives in the portal Layout so it keeps
 * running on every page (Dashboard, Patients, etc.) as long as the user has
 * the chime toggle enabled on the Appointments page.
 *
 * - Reads enabled flag from localStorage and listens for changes (storage event).
 * - Fetches today's sessions on mount, and again when the user comes back to
 *   the tab (throttled). It does NOT poll.
 * - Every 20 sec, checks if any session started in the last CHIME_WINDOW_MIN
 *   minutes and plays the airport-style chime twice (announceTwice), once per
 *   session.
 * - Audio still needs a user gesture to unlock: the first click or key press
 *   anywhere on the page does it (armAnnouncerUnlock), as does the toggle.
 *
 * Why it stopped polling: this component used to refresh every 5 minutes, and
 * because Layout mounts it on every portal page, it did so in every open tab
 * whether or not anyone was looking. The endpoint it calls is the single most
 * expensive one in the app (`/admin/appointment`, ~118 queries), so an idle
 * office with a few tabs open was spending thousands of database queries an
 * hour announcing nothing. On 2026-09-04 the host's abuse prevention revoked
 * the database user's grants for exactly that kind of volume.
 *
 * Fetching only on mount would not have worked either: Layout is persistent, so
 * it does not remount when you navigate between pages — a tab opened at 9am
 * would hold a 9am list all day and never chime for anything booked later.
 * Coming back to the tab is the moment that actually matters, so that is the
 * trigger.
 */
const GlobalSessionAnnouncer = () => {
  // Defaults to ON (unmuted) for new users who haven't set a preference yet.
  const [chimeOn, setEnabled] = useState(
    () => typeof window === "undefined" || localStorage.getItem(STORAGE_KEY) !== "0"
  );
  // Today's sessions come from the booking grid (POST /admin/appointment, view_scheduling). A
  // role without it got a 403 on every page load and tab return, for nothing to announce.
  const user = useSelector((state) => state.auth?.user);
  const permissions = useSelector((state) => state.auth?.permissions) || [];
  const readsGrid = user?.RoleId === 1 || user?.RoleId === 2 || permissions.includes("view_scheduling");
  const enabled = chimeOn && readsGrid;
  const sessionsRef = useRef([]);
  const announcedRef = useRef(new Set());
  const lastFetchDateRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  // Lets the chime tick trigger a refetch at midnight without depending on the
  // fetch effect, which would restart the tick every time the list changed.
  const fetchRef = useRef(null);

  // ── Listen to localStorage changes (toggle from Appointments page) ──
  useEffect(() => {
    const sync = () => setEnabled(localStorage.getItem(STORAGE_KEY) !== "0");
    window.addEventListener("storage", sync);
    // Custom event so same-tab toggle updates instantly (storage event only fires cross-tab)
    window.addEventListener("vistaar:announcer-toggle", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("vistaar:announcer-toggle", sync);
    };
  }, []);

  // ── Unlock the audio on the first click or key press, while the chime is on ──
  useEffect(() => {
    if (!enabled) return undefined;
    return armAnnouncerUnlock();
  }, [enabled]);

  // ── Fetch today's sessions on mount, and on returning to the tab ──
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const fetchToday = async ({ force = false } = {}) => {
      const now = Date.now();
      if (!force && now - lastFetchAtRef.current < REFRESH_MIN_GAP_MS) return;
      // Stamped before the request, not after, so two triggers arriving together
      // (visibilitychange and focus both fire when you alt-tab back) cannot both
      // get through.
      lastFetchAtRef.current = now;

      const today = moment().format("YYYY-MM-DD");
      try {
        const res = await appointmentService.getList({ date: today });
        if (cancelled) return;
        if (res?.rescode === 1) {
          sessionsRef.current = (res.data?.SESSIONS || []).map((s) => ({
            id: s.appointment_id || `${s.patient_id}-${s.time}-${s.doctor}`,
            name: s.name,
            time: s.time,
            sectionDisable: s.sectionDisable,
          }));
          // Reset announced set if date changed since last fetch
          if (lastFetchDateRef.current !== today) {
            announcedRef.current = new Set();
            lastFetchDateRef.current = today;
          }
        }
      } catch (_) {
        // silent — announcer is a non-critical background feature
      }
    };

    fetchRef.current = fetchToday;
    fetchToday({ force: true });

    // A hidden tab is nobody looking at it; there is nothing to announce and no
    // reason to ask the server anything.
    const onBackToTab = () => {
      if (document.visibilityState === "hidden") return;
      fetchToday();
    };
    document.addEventListener("visibilitychange", onBackToTab);
    window.addEventListener("focus", onBackToTab);

    return () => {
      cancelled = true;
      fetchRef.current = null;
      document.removeEventListener("visibilitychange", onBackToTab);
      window.removeEventListener("focus", onBackToTab);
    };
  }, [enabled]);

  // ── Watch current time and chime on match ──
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      // A tab left open overnight would otherwise keep yesterday's list and
      // chime yesterday's times against today's clock. The 5-minute poll used
      // to hide this; without it the check has to be explicit.
      const today = moment().format("YYYY-MM-DD");
      if (lastFetchDateRef.current && lastFetchDateRef.current !== today) {
        announcedRef.current = new Set();
        sessionsRef.current = [];
        // Claim the new date here rather than leaving it to fetchToday, which
        // only stamps it on a successful response. A refetch that failed — or
        // returned anything but rescode 1 — left this branch true, so the next
        // tick fired another forced request, and the next: the 20-second poll
        // this component exists to avoid, running forever in every open tab.
        // One attempt per date change is the whole contract; the tab-return
        // trigger is what recovers a day whose refetch did not land.
        lastFetchDateRef.current = today;
        if (fetchRef.current) fetchRef.current({ force: true });
        return;
      }

      if (!isAnnouncerUnlocked()) return;
      const nowMin = minutesOf(moment().format("HH:mm"));
      sessionsRef.current.forEach((s) => {
        if (!s || s.sectionDisable === 1) return;
        const startMin = minutesOf(s.time);
        if (startMin === null || nowMin - startMin < 0 || nowMin - startMin > CHIME_WINDOW_MIN) return;
        const key = String(s.id);
        if (announcedRef.current.has(key)) return;
        announcedRef.current.add(key);
        announceTwice({ patientName: s.name });
      });
    };
    tick();
    const id = setInterval(tick, CHIME_TICK_MS);
    return () => clearInterval(id);
  }, [enabled]);

  return null;
};

export default GlobalSessionAnnouncer;
